import React, { useCallback, useEffect, useState } from "react";
import { MenuItem } from "./MenuItem";
import { NavLink } from "react-router-dom";
import { ApplicationState } from "../../../../../src/store";
import { useDispatch, useSelector } from "react-redux";
import { getDialogDimensions } from "../../../../../src/helper/HelperFunctions";
import { HelpIcon, LogOutIcon } from "../../../../assets/images/svg/SideMenuIconCollection";
import { signoutRedirect } from "../../../../../src/oidcClient/userService";
import { logger } from "../../../../../src/routes/LoggedIn";
import { appInsightsData } from "../../../../../src/helper/AppInsightsData";
import { requestHelpCenterUrl } from "../../../../../src/actionCreators/commonActionCreators";
import {
    fetchReceivedMessageHistorySettings,
    fetchRequestHistorySettings,
    fetchSentMessageHistorySettings,
} from "../../../../../src/actionCreators/adminSettingsActionCreators";
import { ISideMenuItem } from "../../../../../src/Core/ViewModels/SideMenu/SideMenuModel";
import { SideMenuConstants, dialogRoutes, restrictedMenuItemCaptions } from "../../../../../src/helper/Constants";
import { storeUserError } from "../../../../../src/actions/authActions";
import { ExchangeIcon } from "src/assets/images/svg/ProductLogoCollection";

interface ISideMenuProps {
    onComposeClick?: () => void;
    closeSideMenuHandler: () => void;
}

interface ISideMenuState {
    menuModel: ISideMenuItem[];
    toggleClass: string | null;
    activeMenu: string | null;
    helpMenu: ISideMenuItem;
}

export const SideMenu: React.FC<ISideMenuProps> = props => {
    const dispatch = useDispatch();
    const { mailboxUserData } = useSelector((state: ApplicationState) => state.commonState);
    const [state, setState] = useState<ISideMenuState>({
        menuModel: [],
        toggleClass: null,
        activeMenu: null,
        helpMenu: null,
    });
    const { exchangeEnabled } = useSelector((state: ApplicationState) => state.postAuthState);
    const sideMenuData = useSelector((state: ApplicationState) => state.commonState.sideMenuData);
    const messageHistoryStore = useSelector(
        (state: ApplicationState) => state.adminSettingsState.messageHistorySettings
    );
    const requestHistoryStore = useSelector(
        (state: ApplicationState) => state.adminSettingsState.requestHistorySettings
    );
    const fetchMenuDetails = useCallback(() => {
        const menuData = [];
        let helpMenu;
        sideMenuData.map(section => {
            if (section.items.length > 0) {
                section.items.map(menu => {
                    if (menu.caption === SideMenuConstants.HELP) {
                        helpMenu = menu;
                        return;
                    }
                    if (mailboxUserData && restrictedMenuItemCaptions.includes(menu.caption)) {
                        return;
                    }
                    if (menu.caption === SideMenuConstants.ADMIN_SETTINGS) {
                        if (!menu.disabled) {
                            const adminSettingsItem = { ...menu };
                            adminSettingsItem.items.length > 0
                                ? menuData.push({ ...adminSettingsItem, items: [] })
                                : [];
                        }

                        return;
                    }
                    if (menu.caption === SideMenuConstants.MESSAGE_HISTORY) {
                        const items = [...menu.items];
                        const messageHistoryItem = { ...menu };
                        messageHistoryItem.disabled = items[0].disabled && items[1].disabled;
                        const sentItemAccess = messageHistoryStore.sentItemsSettings?.isUserAllowedToViewReports;
                        const receivedItemAccess =
                            messageHistoryStore.receivedItemsSettings
                                ?.isUserAllowedToViewReceivedMessageHistoricalReport;
                        //TO DO: Get the value from MessageHistory Store
                        const documentRequestAccess =
                            requestHistoryStore?.isUserAllowedToViewDocumentRequestHistoricalReport;
                        if (!sentItemAccess) {
                            removeReportItem(items, "Sent Messages");
                        }
                        if (!receivedItemAccess) {
                            removeReportItem(items, "Received Messages");
                        }
                        if (!documentRequestAccess) {
                            removeReportItem(items, "Document Requests");
                        }
                        messageHistoryItem.items.length > 0 ? menuData.push({ ...messageHistoryItem, items: items }) : [];
                        return;
                    }
                    menuData.push(menu);
                });
            }
        });

        setState({
            ...state,
            menuModel: menuData,
            helpMenu: helpMenu,
        });
    }, [sideMenuData, messageHistoryStore ,requestHistoryStore]);

    const removeReportItem = (items: ISideMenuItem[], caption: string) => {
        const index = items?.findIndex(item => item.caption === caption) ?? 0;
        items.splice(index, 1);
    };

    useEffect(() => {
        fetchMenuDetails();
        dispatch(requestHelpCenterUrl());
    }, [mailboxUserData, sideMenuData ,requestHistoryStore, messageHistoryStore]);

    useEffect(() => {
        if (exchangeEnabled) {
            dispatch(fetchSentMessageHistorySettings());
            dispatch(fetchReceivedMessageHistorySettings());
            dispatch(fetchRequestHistorySettings());
        }
    }, [exchangeEnabled]);

    useEffect(() => {
        state.menuModel?.length > 0 &&
            state.menuModel.map(menu => {
                if (menu.route === window.location.pathname) {
                    setState({
                        ...state,
                        activeMenu: menu.caption,
                    });
                }
            });
    }, [state.menuModel, window.location.pathname]);

    const toggleSettingsDialog = () => {
        const url = `${window.location.origin}/settings/general`;
        Office.context.ui.displayDialogAsync(url, getDialogDimensions(), asyncResult => {
            const dialog = asyncResult.value;
            dialog.addEventHandler(
                Office.EventType.DialogMessageReceived,
                async (arg: { message: string; origin: string }) => arg.message !== "logout" && dialog.close()
            );
        });
    };

    const toggleUnlockDialog = () => {
        const url = `${window.location.origin}/unlock`;
        Office.context.ui.displayDialogAsync(url, getDialogDimensions(), asyncResult => {
            const dialog = asyncResult.value;
            dialog.addEventHandler(
                Office.EventType.DialogMessageReceived,
                async (arg: { message: string; origin: string }) => arg.message !== "logout" && dialog.close()
            );
        });
    };

    const onSelectMenu = (menu: string) => {
        if (menu !== SideMenuConstants.MESSAGE_HISTORY) {
            props.closeSideMenuHandler();
        }
        if (menu === SideMenuConstants.ADMIN_SETTINGS) {
            toggleSettingsDialog();
        } else if (menu === SideMenuConstants.UNLOCK) {
            toggleUnlockDialog();
        }
        state.toggleClass === null
            ? setState({
                  ...state,
                  activeMenu: menu === state.activeMenu ? null : menu,
              })
            : null;
    };

    const onLogoutToggle = () => {
        // signoutRedirect();

        const url = `${window.location.origin}/logout`;
        let dialog;
        Office.context.ui.displayDialogAsync(url, getDialogDimensions(false), asyncResult => {
            dialog = asyncResult.value;
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                const error = asyncResult.error;
                if (error.code == 12009) {
                    dispatch(storeUserError(error.message));
                }
            } else {
                dialog.addEventHandler(
                    Office.EventType.DialogMessageReceived,
                    async (arg: { message: string; origin: string }) => {
                        const response = arg.message;
                        if (response === "logout") {
                            dialog.close();
                            await signoutRedirect();
                        }

                        window.location.reload();
                    }
                );
            }
        });
    };
    const triggerPageViewEvent = () => {
        logger.trackPageView(appInsightsData.Help.PageTitle);
    };

    return (
        <ul className={"sidebar navbar-nav"}>
            <NavLink to="" className="navbar-brand">
                <ExchangeIcon height={25} width={113} style={{color: "#fff", margin: "10px 0 10px 10px"}}/>
            </NavLink>
            <div className="side-menu-top-container">
                {exchangeEnabled && (
                    <>
                        <li
                            key="mailbox"
                            className={"nav-item layout-dropdown " + state.toggleClass}
                            onMouseLeave={() => {
                                document.getElementsByClassName("toggled")[0] &&
                                    setState({ ...state, toggleClass: null });
                            }}
                            onMouseEnter={() => {
                                document.getElementsByClassName("toggled")[0] &&
                                    setState({ ...state, toggleClass: "show" });
                            }}
                        ></li>
                        {state.menuModel &&
                            state.menuModel.map((menu, index) => {
                                return (
                                    <MenuItem
                                        {...menu}
                                        key={`${menu.caption}-${index}-${menu.route}`}
                                        active={state.activeMenu === menu.caption}
                                        caption={menu.caption}
                                        disabled={menu.disabled}
                                        icon={menu.icon}
                                        route={dialogRoutes.includes(menu.caption) ? "" : menu.route}
                                        items={menu.items}
                                        onSelectMenu={onSelectMenu}
                                    />
                                );
                            })}
                    </>
                )}
            </div>
            <div className="bottom-menu">
                <li className="nav-item layout-dropdown" onClick={triggerPageViewEvent}>
                    <a className="nav-link" role="button" href={state.helpMenu?.route} target="_blank" rel="noreferrer">
                        <HelpIcon height={25} width={25} color={"#fff"} />
                        <span>{state.helpMenu?.caption ?? "Help"}</span>
                    </a>
                </li>
                {!mailboxUserData && (
                    <>
                        <hr />
                        <li className="nav-item layout-dropdown menu-logout">
                            <a className="nav-link" role="button" onClick={onLogoutToggle}>
                                <LogOutIcon width={24} height={24} color="#fff" />
                                <span>Logout</span>
                            </a>
                        </li>
                    </>
                )}
            </div>
        </ul>
    );
};

export default SideMenu;
